// Day-scoped cache for large corpora (listings, rentals). Entries expire at
// the next 09:00 local — aligned with the 08:30 daily scrape job (same idiom
// the pages used inline before). Backend is IndexedDB (localStorage quotas
// ~5 MB are too small for the ~28 MB listings corpus); jsdom and ancient
// browsers fall back to localStorage with a size guard instead of the old
// silent catch-and-lose behaviour.

const DB_NAME = "immo-watch";
const STORE_NAME = "daycache";
// localStorage fallback only: skip writes above ~4 MB so we never throw
// QuotaExceededError against the shared 5 MB origin quota.
export const LOCAL_SIZE_LIMIT = 4 * 1024 * 1024;

function nextExpiry() {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  if (Date.now() >= d.getTime()) d.setDate(d.getDate() + 1);
  return d.getTime();
}

function hasIDB() {
  try {
    return typeof indexedDB !== "undefined" && !!indexedDB;
  } catch {
    return false;
  }
}

// Exposed for tests/diagnostics: which storage backend is active.
export function _backend() {
  return hasIDB() ? "indexeddb" : "localstorage";
}

let dbPromise = null;
function openDB() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function idbRequest(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return idbRequest(db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(key));
}

async function idbPut(key, entry) {
  const db = await openDB();
  return idbRequest(db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).put(entry, key));
}

const warnedKeys = new Set();
function localPut(key, entry) {
  const raw = JSON.stringify(entry);
  if (raw.length > LOCAL_SIZE_LIMIT) {
    if (!warnedKeys.has(key)) {
      warnedKeys.add(key);
      console.warn(`dayCache: "${key}" is ${(raw.length / 1024 / 1024).toFixed(1)} MB — over the localStorage fallback limit, not cached`);
    }
    return;
  }
  try {
    localStorage.setItem(key, raw);
  } catch (e) {
    if (!warnedKeys.has(key)) {
      warnedKeys.add(key);
      console.warn(`dayCache: localStorage write for "${key}" failed`, e);
    }
  }
}

function localGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // corrupt JSON counts as a miss
  }
}

export async function getDayCache(key) {
  let entry = null;
  if (hasIDB()) {
    try {
      entry = await idbGet(key);
    } catch {
      entry = null;
    }
  } else {
    entry = localGet(key);
  }
  if (!entry || typeof entry.expiresAt !== "number") return null;
  if (Date.now() >= entry.expiresAt) return null;
  return entry.payload ?? null;
}

export async function setDayCache(key, payload) {
  const entry = { expiresAt: nextExpiry(), payload };
  if (hasIDB()) {
    try {
      await idbPut(key, entry);
    } catch (e) {
      console.warn(`dayCache: IndexedDB write for "${key}" failed`, e);
    }
  } else {
    localPut(key, entry);
  }
}
